import React, { useMemo } from 'react';
import PositionItem from '../components/PositionItem';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const { FiClock, FiInbox, FiPieChart, FiTrendingUp, FiActivity, FiArrowUpCircle, FiArrowDownCircle, FiLayers, FiTarget, FiBarChart2 } = FiIcons;

// Custom Tooltip for the Equity Curve Chart
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-[#0B0E14] border border-slate-800 p-3 rounded-xl shadow-xl min-w-[150px]">
        <p className="text-slate-200 font-bold mb-2 border-b border-slate-800 pb-2">
          {data.name} <span className="text-slate-500 font-normal">({data.token})</span>
        </p>
        <div className="space-y-1">
          <p className="text-xs text-slate-400 flex justify-between gap-4">
            Trade PnL: <span className={data.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
              {data.pnl >= 0 ? '+' : ''}{data.pnl.toFixed(4)} SOL
            </span>
          </p>
          <p className="text-xs text-slate-400 flex justify-between gap-4 font-bold">
            Net Equity: <span className="text-purple-400">
              {data.equity.toFixed(4)} SOL
            </span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export default function Trades({ positions, onDeletePosition }) {
  const closedPositions = positions.filter(p => p.status === 'CLOSED');

  // Calculate Advanced Performance Analytics & Chart Data
  const { stats, chartData } = useMemo(() => {
    let wins = 0;
    let netPnlSol = 0;
    let totalVolume = 0;
    let bestTrade = -Infinity;
    let worstTrade = Infinity;
    let grossProfit = 0;
    let grossLoss = 0;
    
    let chartDataPoints = [];
    let currentEquity = 0;

    if (closedPositions.length > 0) {
      // Sort oldest to newest for the chart timeline
      const chronological = [...closedPositions].reverse();

      chronological.forEach((pos, index) => {
        // Prefer realized PnL from the actual on-chain fill (LIVE); otherwise
        // fall back to the price-based estimate (PAPER or missing data).
        const cost = pos.costSol != null ? pos.costSol : pos.amount;
        const hasRealized = pos.realizedSol != null && cost;
        const pnlSol = hasRealized
          ? pos.realizedSol - cost
          : (pos.amount ? (pos.amount * (pos.currentPrice / pos.entryPrice)) - pos.amount : 0);
        // Return on capital from actual SOL in/out — immune to entry_price feed glitches that
        // otherwise misclassify a breakeven/winning trade as a −100% loss.
        const pnlPercent = hasRealized
          ? (pnlSol / cost) * 100
          : (pos.entryPrice ? ((pos.currentPrice - pos.entryPrice) / pos.entryPrice) * 100 : 0);

        if (pnlPercent > 0) {
          wins++;
          grossProfit += pnlSol;
        } else {
          grossLoss += Math.abs(pnlSol);
        }

        netPnlSol += pnlSol;
        totalVolume += (pos.amount || 0);

        if (pnlPercent > bestTrade) bestTrade = pnlPercent;
        if (pnlPercent < worstTrade) worstTrade = pnlPercent;

        currentEquity += pnlSol;
        chartDataPoints.push({
          name: `Trade ${index + 1}`,
          token: pos.tokenSymbol || 'Unknown',
          pnl: pnlSol,
          equity: currentEquity
        });
      });
    }

    const total = closedPositions.length;
    const losses = total - wins;
    const overallPnlPercent = totalVolume > 0 ? (netPnlSol / totalVolume) * 100 : 0;

    // Advanced Metrics
    const profitFactor = grossLoss === 0 ? (grossProfit > 0 ? Infinity : 0) : (grossProfit / grossLoss);
    const averageWin = wins > 0 ? grossProfit / wins : 0;
    const averageLoss = losses > 0 ? grossLoss / losses : 0;

    return {
      stats: {
        total,
        wins,
        losses,
        winRate: total > 0 ? (wins / total) * 100 : 0,
        netPnlSol,
        overallPnlPercent,
        bestTrade: bestTrade === -Infinity ? 0 : bestTrade,
        worstTrade: worstTrade === Infinity ? 0 : worstTrade,
        totalVolume,
        profitFactor,
        averageWin,
        averageLoss
      },
      chartData: chartDataPoints
    };
  }, [closedPositions]);

  const formatPercent = (val) => `${val > 0 ? '+' : ''}${val.toFixed(2)}%`;
  const formatSOL = (val) => `${val > 0 ? '+' : ''}${val.toFixed(4)}`;
  const getPnlColor = (val) => {
    if (val > 0) return 'text-green-400';
    if (val < 0) return 'text-red-400';
    return 'text-slate-200';
  };

  return (
    <div className="h-full overflow-y-auto custom-scrollbar pr-2">
      <div className="max-w-7xl mx-auto space-y-6 pb-12">
        {/* Header */}
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiBarChart2} className="text-purple-400 text-2xl" />
          <h2 className="text-xl font-bold text-white">Historical Trade Analysis</h2>
        </div>

        {closedPositions.length > 0 ? (
          <>
            {/* Equity Curve Chart */}
            <div className="glass-panel p-6 rounded-2xl w-full">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiTrendingUp} className="text-blue-400 text-lg" />
                  <h3 className="text-lg font-bold text-white">Cumulative Equity Curve (SOL)</h3>
                </div>
                <div className={cn("text-lg font-black", getPnlColor(stats.netPnlSol))}>
                  Total: {formatSOL(stats.netPnlSol)} SOL
                </div>
              </div>
              
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      stroke="#64748b" 
                      fontSize={10} 
                      tickLine={false} 
                      axisLine={false}
                      minTickGap={20}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      fontSize={10} 
                      tickLine={false} 
                      axisLine={false}
                      tickFormatter={(val) => val.toFixed(2)}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{stroke: '#334155', strokeWidth: 1, strokeDasharray: '4 4'}} />
                    <Area 
                      type="monotone" 
                      dataKey="equity" 
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorEquity)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Advanced Analytics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              
              {/* Win Rate */}
              <div className="glass-panel p-4 rounded-xl border-l-4 border-l-blue-500 flex flex-col justify-center">
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiPieChart} className="text-blue-400" /> Win Rate
                </div>
                <div className="text-2xl font-black text-white">{stats.winRate.toFixed(1)}%</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">{stats.wins} Won / {stats.losses} Lost</div>
              </div>

              {/* Profit Factor */}
              <div className="glass-panel p-4 rounded-xl border-l-4 border-l-purple-500 flex flex-col justify-center">
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiTarget} className="text-purple-400" /> Profit Factor
                </div>
                <div className="text-2xl font-black text-white">
                  {stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)}
                </div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Gross Profit / Gross Loss</div>
              </div>

              {/* Net PnL (SOL) */}
              <div className={cn("glass-panel p-4 rounded-xl border-l-4 flex flex-col justify-center", stats.netPnlSol >= 0 ? "border-l-green-500" : "border-l-red-500")}>
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiActivity} className={stats.netPnlSol >= 0 ? "text-green-400" : "text-red-400"} /> Net PnL (SOL)
                </div>
                <div className={cn("text-2xl font-black", getPnlColor(stats.netPnlSol))}>{formatSOL(stats.netPnlSol)}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Total Profit/Loss</div>
              </div>

              {/* Net ROI */}
              <div className={cn("glass-panel p-4 rounded-xl border-l-4 flex flex-col justify-center", stats.overallPnlPercent >= 0 ? "border-l-green-500" : "border-l-red-500")}>
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiTrendingUp} className={stats.overallPnlPercent >= 0 ? "text-green-400" : "text-red-400"} /> Net ROI
                </div>
                <div className={cn("text-2xl font-black", getPnlColor(stats.overallPnlPercent))}>{formatPercent(stats.overallPnlPercent)}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Return on Capital</div>
              </div>

              {/* Average Win */}
              <div className="glass-panel p-4 rounded-xl border-l-4 border-l-green-500 flex flex-col justify-center">
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiArrowUpCircle} className="text-green-400" /> Avg Win
                </div>
                <div className="text-2xl font-black text-green-400">+{stats.averageWin.toFixed(4)}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Average Profit per Win</div>
              </div>

              {/* Average Loss */}
              <div className="glass-panel p-4 rounded-xl border-l-4 border-l-red-500 flex flex-col justify-center">
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiArrowDownCircle} className="text-red-400" /> Avg Loss
                </div>
                <div className="text-2xl font-black text-red-400">-{stats.averageLoss.toFixed(4)}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Average Loss per Defeat</div>
              </div>

              {/* Best Trade */}
              <div className="glass-panel p-4 rounded-xl border-l-4 border-l-green-500 flex flex-col justify-center">
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiArrowUpCircle} className="text-green-400" /> Best Trade
                </div>
                <div className="text-2xl font-black text-green-400">{formatPercent(stats.bestTrade)}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Highest Single ROI</div>
              </div>

              {/* Worst Trade */}
              <div className="glass-panel p-4 rounded-xl border-l-4 border-l-red-500 flex flex-col justify-center">
                <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SafeIcon icon={FiArrowDownCircle} className="text-red-400" /> Worst Trade
                </div>
                <div className="text-2xl font-black text-red-400">{formatPercent(stats.worstTrade)}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">Lowest Single ROI</div>
              </div>

            </div>
          </>
        ) : null}

        {/* Trade History List Container */}
        <div className="glass-panel rounded-2xl p-6 flex flex-col min-h-[400px]">
          <div className="flex items-center justify-between mb-6 flex-shrink-0">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiClock} className="text-purple-400 text-xl" />
              <h2 className="text-lg font-bold text-white">Trade History Log</h2>
            </div>
            <div className="bg-slate-800 px-3 py-1 rounded-full text-[10px] font-bold text-slate-300 uppercase tracking-wider">
              {closedPositions.length} Completed
            </div>
          </div>

          <div className="flex-1 space-y-4">
            {closedPositions.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-600 py-20">
                <SafeIcon icon={FiInbox} className="text-5xl opacity-10 mb-4" />
                <p className="text-sm font-medium">No completed trades yet.</p>
                <p className="text-xs">Your closed positions and analytics will appear here.</p>
              </div>
            ) : (
              closedPositions.map(pos => (
                <PositionItem 
                  key={pos.id} 
                  position={pos} 
                  onManualSell={() => {}} 
                  onDeletePosition={onDeletePosition}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}